import {
  classicTotals,
  UsageEntry,
  UsagePage,
  UsageSource,
} from "../api/usage";

export type SpendDay = { day: string; tokens: number };
export type SpendActivity = {
  id: string;
  label: string;
  day: string;
  tokens: number;
  refunded: number;
  unpriced: number;
  storyId?: string;
};
export const formatTokens = (value: number) =>
  value > 0 && value < 0.01
    ? "<0.01"
    : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
export const formatUsageDay = (day: string) =>
  new Date(day + "T12:00:00Z").toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
export function usageWindow(days: number, now = new Date()) {
  const end = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1),
  );
  const start = new Date(end.getTime() - days * 86400000);
  return { from: start.toISOString(), to: end.toISOString() };
}
const positive = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? Math.max(0, value) : 0;
const category = (kind: string) =>
  ({ model: "Chat", image: "Images", audio: "Audio", video: "Video" })[kind] ||
  "Other activity";
function creationLabel(entry: UsageEntry) {
  if (entry.storyId) return entry.storyTitle || "Story";
  const reason = entry.reason || "";
  if (/voice|audio|music/i.test(reason)) return "Audio";
  if (/image|cover|character/i.test(reason)) return "Images";
  if (/video/i.test(reason)) return "Video";
  if (/book/i.test(reason)) return "Books";
  if (/story/i.test(reason)) return "Stories";
  return "Other creations";
}
export function spendView(
  page: UsagePage | undefined,
  source: UsageSource,
  range: { from: string; to: string },
) {
  const days: SpendDay[] = [];
  for (
    let time = Date.parse(range.from);
    time < Date.parse(range.to);
    time += 86400000
  )
    days.push({ day: new Date(time).toISOString().slice(0, 10), tokens: 0 });
  const dayMap = new Map(days.map((day) => [day.day, day]));
  const grouped = new Map<string, SpendActivity>();
  let total = 0,
    refunded = 0,
    unpriced = 0;
  const add = (
    id: string,
    label: string,
    day: string,
    tokens: number,
    refund: number,
    pending: number,
    storyId?: string,
  ) => {
    if (!dayMap.has(day)) return;
    total += tokens;
    refunded += refund;
    unpriced += pending;
    dayMap.get(day)!.tokens += tokens;
    const row = grouped.get(id) || {
      id,
      label,
      day,
      tokens: 0,
      refunded: 0,
      unpriced: 0,
      storyId,
    };
    row.tokens += tokens;
    row.refunded += refund;
    row.unpriced += pending;
    if (day > row.day) row.day = day;
    grouped.set(id, row);
  };
  if (source === "gambit" && page?.spend) {
    for (const row of page.spend)
      add(
        `${row.day}:${row.kind}`,
        category(row.kind),
        row.day,
        positive(row.tokens),
        positive(row.refunded),
        positive(row.unpriced),
      );
  } else {
    for (const entry of page?.entries || []) {
      const time = Date.parse(entry.createdAt);
      if (!Number.isFinite(time)) continue;
      const day = new Date(time).toISOString().slice(0, 10);
      if (source === "classic") {
        const amount = classicTotals([entry]);
        if (!amount.spent && !amount.refunded) continue;
        const label = creationLabel(entry);
        add(
          entry.storyId ? `story:${entry.storyId}` : `creation:${label}`,
          label,
          day,
          amount.spent,
          amount.refunded,
          0,
          entry.storyId,
        );
      } else {
        add(
          `${day}:${entry.kind}`,
          category(entry.kind),
          day,
          positive(entry.fableTokens),
          0,
          typeof entry.fableTokens === "number" &&
            Number.isFinite(entry.fableTokens)
            ? 0
            : 1,
        );
      }
    }
  }
  // The server summary covers the whole requested period, independently of
  // which chronological Classic history pages have been loaded.
  if (source === "classic" && page?.spend) {
    total = 0; refunded = 0;
    for (const day of days) day.tokens = 0;
    for (const row of page.spend) {
      const day = dayMap.get(row.day);
      if (!day) continue;
      total += positive(row.tokens);
      refunded += positive(row.refunded);
      day.tokens += positive(row.tokens);
    }
  }
  return {
    total,
    refunded,
    unpriced,
    days,
    activity: [...grouped.values()].sort(
      (a, b) => b.day.localeCompare(a.day) || b.tokens - a.tokens,
    ),
    partial: !!page?.nextCursor && !page.spend,
  };
}
